import {
    Box,
    Button,
    Form,
    Link,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TextInput,
} from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { hasOneAuthorization } from '@frontend/utils';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import type { IDermaAppRegistration } from '../../interfaces/dermaAppRegistration';
import { tryParseInt } from '../../../../common/utils/tryParseInt';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { FullName, HeaderGroup, Loader } from '../../elements/common';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDatetime } from '../../elements/NullableFormattedDatetime';

export function DermaAppRegistrationsList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [practitioner, setPractitioner] = useState<string | null>(null);
    const [isSearching, setIsSearching] = useState(true);
    const [total, setTotal] = useState(0);
    const [registrations, setRegistrations] = useState<IDermaAppRegistration[]>([]);
    const [currentPage, setCurrentPage] = useState(tryParseInt(searchParams.get('page') ?? undefined, 1));

    useEffect(() => {
        assertHasAuthorization(ManagerAuthorization.dermaAppRegistrationLogs);
        search();
    }, [currentPage]);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translateKey('dermaApp.registrations')} />
            <Form
                onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    if (currentPage !== 1) {
                        setCurrentPage(1);
                    } else {
                        search();
                    }
                }}
                submitOnEnter={true}
            >
                <Box
                    alignItems="start"
                    display="grid"
                    gap={3}
                    gridTemplateColumns={['1fr', '1fr max-content', null, '380px max-content']}
                    mb={4}
                >
                    <TextInput
                        onChange={(e) => setPractitioner(e.target.value)}
                        placeholder={translateKey('practitioner.practitioner')}
                        value={practitioner ?? ''}
                    />
                    <Button type="submit">{translateKey('common.search')}</Button>
                </Box>
            </Form>

            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('practitioner.practitioner')}</TableHeader>
                            <TableHeader>{translateKey('dermaApp.deviceId')}</TableHeader>
                            <TableHeader>{translateKey('common.startDate')}</TableHeader>
                            <TableHeader>{translateKey('common.endDate')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!isSearching &&
                            registrations.length > 0 &&
                            registrations.map((registration) => (
                                <TableRow
                                    key={registration.id}
                                    onClick={() => navigate(`/dermaAppRegistrations/${registration.id}/logins`)}
                                >
                                    <TableCell>
                                        {hasOneAuthorization(ManagerAuthorization.practitionerRead) ? (
                                            <Link
                                                onClick={(e) => e.stopPropagation()}
                                                to={`/practitioners/${registration.practitionerId}/profile`}
                                                variant="link"
                                            >
                                                <FullName {...registration.practitioner} />
                                            </Link>
                                        ) : (
                                            <FullName {...registration.practitioner} />
                                        )}
                                    </TableCell>
                                    <TableCell>{registration.deviceId}</TableCell>
                                    <TableCell>
                                        <NullableFormattedDatetime value={registration.startDatetime} />
                                    </TableCell>
                                    <TableCell>
                                        <NullableFormattedDatetime value={registration.endDatetime} />
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {isSearching && <Loader centered />}
            {!isSearching && registrations.length === 0 && (
                <HeaderGroup
                    alignment={'center'}
                    header={translateKey('common.noResult')}
                />
            )}
            <ListPager
                isDisabled={isSearching}
                onChangePage={handleNavigation}
                page={currentPage}
                total={total}
            />
        </PageTemplate>
    );

    async function handleNavigation(page: number) {
        navigate(`/dermaAppRegistrations?page=${page}`);
        setCurrentPage(page);
    }

    async function search() {
        setIsSearching(true);
        let url = `/api/dermaAppRegistrations?page=${currentPage}`;

        if (practitioner) {
            url += `&practitioner=${encodeURIComponent(practitioner)}`;
        }

        const result = await fetchJson(url, 'GET');

        setTotal(result.json.total);
        setRegistrations(result.json.items);
        setIsSearching(false);
    }
}
